import React, { useState, useEffect } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Activity, Search, Clock, User, FileText, Share2, 
  LogIn, UserPlus, Download, Upload, Trash2, Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogsPage = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        let url = `${API_URL}/api/audit-logs?limit=500`;
        if (actionFilter !== 'all') {
          url += `&action=${actionFilter}`;
        }
        
        const response = await fetch(url, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (response.ok) {
          const data = await response.json();
          setLogs(data);
        }
      } catch (error) {
        console.error('Failed to fetch audit logs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [token, actionFilter]);

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return <LogIn className="w-4 h-4" />;
      case 'register': return <UserPlus className="w-4 h-4" />;
      case 'document_upload': return <Upload className="w-4 h-4" />;
      case 'document_download': return <Download className="w-4 h-4" />;
      case 'document_delete': return <Trash2 className="w-4 h-4" />;
      case 'access_grant': return <Share2 className="w-4 h-4" />;
      case 'access_revoke': return <Shield className="w-4 h-4" />;
      case 'user_update': return <User className="w-4 h-4" />;
      case 'user_delete': return <Trash2 className="w-4 h-4" />;
      case 'password_change': return <Shield className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    if (action.includes('delete') || action.includes('revoke')) {
      return 'text-red-400 bg-red-400/10';
    }
    if (action.includes('login') || action.includes('register')) {
      return 'text-primary bg-primary/10';
    }
    if (action.includes('upload') || action.includes('grant')) {
      return 'text-blue-400 bg-blue-400/10';
    }
    if (action.includes('download')) {
      return 'text-yellow-400 bg-yellow-400/10';
    }
    return 'text-white/60 bg-white/10';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const filteredLogs = logs.filter(log => 
    log.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.user_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    log.details && (typeof log.details === 'string' ? log.details : JSON.stringify(log.details)).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="Audit Logs">
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-logs-input"
            />
          </div>
          
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-48 bg-black/50 border-white/10 text-white">
              <SelectValue placeholder="Filter by action" />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A] border-white/10">
              <SelectItem value="all" className="text-white">All Actions</SelectItem>
              <SelectItem value="login" className="text-white">Logins</SelectItem>
              <SelectItem value="register" className="text-white">Registrations</SelectItem>
              <SelectItem value="document_upload" className="text-white">Uploads</SelectItem>
              <SelectItem value="document_download" className="text-white">Downloads</SelectItem>
              <SelectItem value="document_delete" className="text-white">Deletions</SelectItem>
              <SelectItem value="access_grant" className="text-white">Access Granted</SelectItem>
              <SelectItem value="access_revoke" className="text-white">Access Revoked</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Info Banner */}
        <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 flex items-center gap-3">
          <Activity className="w-5 h-5 text-primary" />
          <p className="text-white/80 text-sm">
            All user activities are logged and stored securely. This includes logins, document operations, and permission changes.
          </p>
        </div>

        {/* Logs List */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="animate-pulse bg-black/40 border border-white/10 rounded-sm p-4 h-24" />
            ))}
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="text-center py-16">
            <Activity className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No Logs Found</h3>
            <p className="text-white/60">
              {searchQuery || actionFilter !== 'all' ? 'Try adjusting your filters' : 'No activity recorded yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log, index) => (
              <div 
                key={log.log_id || index}
                className="bg-black/40 border border-white/10 rounded-sm p-4 hover:border-white/20 transition-colors"
                data-testid={`audit-log-${log.log_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${getActionColor(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4 mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium">{log.user_name}</span>
                        <span className="text-white/40 text-sm">{log.user_email}</span>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-sm capitalize ${getActionColor(log.action)}`}>
                        {log.action?.replace(/_/g, ' ')}
                      </span>
                    </div>
                    
                    {log.details && (
                      <p className="text-white/60 text-sm mb-2">{typeof log.details === 'object' ? JSON.stringify(log.details) : log.details}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-white/40">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(log.timestamp)}
                      </span>
                      {log.ip_address && (
                        <span>IP: {log.ip_address}</span>
                      )}
                      {log.resource_type && (
                        <span className="capitalize">{log.resource_type}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-black/40 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold text-primary">{logs.filter(l => l.action === 'login').length}</p>
            <p className="text-xs text-white/40 font-mono uppercase">Logins</p>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold text-blue-400">{logs.filter(l => l.action === 'document_upload').length}</p>
            <p className="text-xs text-white/40 font-mono uppercase">Uploads</p>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold text-yellow-400">{logs.filter(l => l.action === 'document_download').length}</p>
            <p className="text-xs text-white/40 font-mono uppercase">Downloads</p>
          </div>
          <div className="bg-black/40 border border-white/10 rounded-sm p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{logs.filter(l => l.action.includes('delete')).length}</p>
            <p className="text-xs text-white/40 font-mono uppercase">Deletions</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AuditLogsPage;
